type MaybeReactive<T> = T | Ref<T> | ComputedRef<T> | (() => T);

type PersistedFetchOpts = {
  key?: MaybeReactive<string>;
  watch?: any[];
  server?: boolean;
  headers?: MaybeReactive<Record<string, string>>;
  credentials?: RequestCredentials;
};

function resolveValue<T>(value: MaybeReactive<T>): T {
  if (typeof value === "function") {
    return (value as () => T)();
  }

  if (value && typeof value === "object" && "value" in value) {
    return (value as Ref<T> | ComputedRef<T>).value;
  }

  return value as T;
}

export async function usePersistedFetch<
  TData = any,
  TVars extends Record<string, any> = Record<string, any>
>(
  operationName: string,
  variables: MaybeReactive<TVars> = {} as TVars,
  opts: PersistedFetchOpts = {}
) {
  const runtimeConfig = useRuntimeConfig();
  const apiUrl = runtimeConfig.public.apiUrl || process.env.GRAPHQL_URL || "https://admin-large.avu.cz/graphql";

  const resolvedKey = computed(() => {
    if (opts.key) return resolveValue(opts.key);
    return `pqfetch:${operationName}:${JSON.stringify(resolveValue(variables))}`;
  });

  const { data, status, error, refresh } = await useAsyncData<TData>(
    resolvedKey,
    async () => {
      const resolvedVars = resolveValue(variables);
      const resolvedHeaders = opts.headers ? resolveValue(opts.headers) : {};

      const res: any = await $fetch(apiUrl, {
        method: "POST",
        headers: {
          "content-type": "application/json",
          ...resolvedHeaders,
        },
        body: {
          operationName,
          variables: resolvedVars,
        },
        credentials: opts.credentials ?? "include",
      });

      if (res?.errors?.length) throw res;

      return res?.data as TData;
    },
    {
      watch: [resolvedKey, ...(opts.watch || [])],
      server: opts.server ?? true,
    }
  );

  const pending = computed(() => status.value === "pending");

  return { data, pending, status, error, refresh };
}
